import * as d3 from 'd3';
import { isNil } from 'lodash';
import { useCallback, useRef } from 'react';
import {
  axisProperties,
  layoutAttributes as layoutConstants,
} from '../graph-viewer/constants';
import {
  formatTickValue,
  getGraphDimensions,
  updateCameraBounds,
} from '../utils';
import type { D3Refs, UseCrossSectionAxesOptions } from './types';

/**
 * Hook to create and update D3 scales and axes for the cross-section profile.
 * @param svgRef - Ref to the SVG element.
 * @param containerRef - Ref to the container element.
 * @param threeJsRefs - Ref to the Three.js refs.
 * @param interactionStateRef - Ref to the interaction state.
 */
export const useCrossSectionAxes = ({
  svgRef,
  containerRef,
  threeJsRefs,
  interactionStateRef,
}: UseCrossSectionAxesOptions) => {
  const d3Refs = useRef<D3Refs>({
    scaleX: null,
    scaleY: null,
  });

  const { marginLeft, marginBottom } = layoutConstants;
  const { minX, xSpacing, padding, minY, ySpacing } = axisProperties.tick;
  const {
    xAxis: xAxisLabel,
    yAxis: yAxisLabel,
    xOffset,
    yOffset,
  } = axisProperties.label;

  const createD3Scales = useCallback(() => {
    const camera = threeJsRefs.current?.camera;
    if (isNil(camera) || isNil(containerRef.current)) {
      return null;
    }

    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    const graphDims = getGraphDimensions(
      containerWidth,
      containerHeight,
      marginLeft,
      marginBottom,
    );

    const scaleX = d3
      .scaleLinear()
      .domain([
        camera.left + camera.position.x,
        camera.right + camera.position.x,
      ])
      .range([0, graphDims.width]);

    const scaleY = d3
      .scaleLinear()
      .domain([
        camera.bottom + camera.position.z,
        camera.top + camera.position.z,
      ])
      .range([graphDims.height, 0]);

    return { scaleX, scaleY };
  }, [threeJsRefs, containerRef]);

  const createAxes = useCallback(
    (
      scaleX: d3.ScaleLinear<number, number>,
      scaleY: d3.ScaleLinear<number, number>,
    ) => {
      if (isNil(containerRef.current)) {
        return null;
      }

      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;
      const graphDims = getGraphDimensions(
        containerWidth,
        containerHeight,
        marginLeft,
        marginBottom,
      );

      const xAxis = d3
        .axisBottom(scaleX)
        .ticks(Math.max(minX, Math.floor(graphDims.width / xSpacing)))
        .tickSize(-graphDims.height)
        .tickPadding(padding)
        .tickFormat((d) => formatTickValue(d as number));

      const yAxis = d3
        .axisLeft(scaleY)
        .ticks(Math.max(minY, Math.floor(graphDims.height / ySpacing)))
        .tickSize(-graphDims.width)
        .tickPadding(padding)
        .tickFormat((d) => formatTickValue(d as number));

      return { xAxis, yAxis };
    },
    [containerRef],
  );

  const initializeSVG = useCallback(() => {
    if (isNil(svgRef.current) || isNil(containerRef.current)) {
      return;
    }

    const scales = createD3Scales();
    if (isNil(scales)) {
      return;
    }

    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    const graphDims = getGraphDimensions(
      containerWidth,
      containerHeight,
      marginLeft,
      marginBottom,
    );

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    d3Refs.current.scaleX = scales.scaleX;
    d3Refs.current.scaleY = scales.scaleY;

    const axes = createAxes(scales.scaleX, scales.scaleY);
    if (isNil(axes)) {
      return;
    }

    // Render X-axis
    svg
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(${marginLeft}, ${graphDims.height})`)
      .call(
        axes.xAxis as unknown as (
          selection: d3.Selection<SVGGElement, unknown, null, undefined>,
        ) => void,
      );

    // Render Y-axis
    svg
      .append('g')
      .attr('class', 'y-axis')
      .attr('transform', `translate(${marginLeft}, 0)`)
      .call(
        axes.yAxis as unknown as (
          selection: d3.Selection<SVGGElement, unknown, null, undefined>,
        ) => void,
      );

    // X-axis label
    svg
      .append('text')
      .attr('class', 'axis-label x-axis-label bold-txt body-txt-4')
      .attr('x', marginLeft + graphDims.width / 2)
      .attr('y', graphDims.height + xOffset)
      .attr('text-anchor', 'middle')
      .text(xAxisLabel);

    // Y-axis label
    svg
      .append('text')
      .attr('class', 'axis-label y-axis-label bold-txt body-txt-4')
      .attr('transform', 'rotate(-90)')
      .attr('x', -graphDims.height / 2)
      .attr('y', yOffset)
      .attr('text-anchor', 'middle')
      .text(yAxisLabel);
  }, [createD3Scales, createAxes, containerRef, svgRef]);

  const updateSVGAxes = useCallback(() => {
    if (isNil(svgRef.current) || isNil(containerRef.current)) {
      return;
    }

    const scales = createD3Scales();
    if (isNil(scales)) {
      return;
    }

    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    const graphDims = getGraphDimensions(
      containerWidth,
      containerHeight,
      marginLeft,
      marginBottom,
    );

    d3Refs.current.scaleX = scales.scaleX;
    d3Refs.current.scaleY = scales.scaleY;

    const svg = d3.select(svgRef.current);
    const axes = createAxes(scales.scaleX, scales.scaleY);
    if (isNil(axes)) {
      return;
    }

    svg
      .select('.x-axis')
      .attr('transform', `translate(${marginLeft}, ${graphDims.height})`)
      .call(
        axes.xAxis as unknown as (
          selection: d3.Selection<d3.BaseType, unknown, null, undefined>,
        ) => void,
      );

    svg
      .select('.y-axis')
      .attr('transform', `translate(${marginLeft}, 0)`)
      .call(
        axes.yAxis as unknown as (
          selection: d3.Selection<d3.BaseType, unknown, null, undefined>,
        ) => void,
      );

    svg
      .select('.x-axis-label')
      .attr('x', marginLeft + graphDims.width / 2)
      .attr('y', graphDims.height + xOffset);

    svg
      .select('.y-axis-label')
      .attr('x', -graphDims.height / 2)
      .attr('y', yOffset);
  }, [createD3Scales, createAxes, containerRef, svgRef]);

  const updateCameraBoundsCallback = useCallback(() => {
    const camera = threeJsRefs.current?.camera;
    if (
      isNil(camera) ||
      isNil(containerRef.current) ||
      isNil(interactionStateRef.current)
    ) {
      return;
    }

    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    const graphDims = getGraphDimensions(
      containerWidth,
      containerHeight,
      marginLeft,
      marginBottom,
    );

    const { scale } = interactionStateRef.current;
    updateCameraBounds(camera, graphDims.width, graphDims.height, scale);
  }, [threeJsRefs, containerRef, interactionStateRef]);

  return {
    d3Refs,
    initializeSVG,
    updateSVGAxes,
    updateCameraBounds: updateCameraBoundsCallback,
  };
};
